import { useState, useEffect } from 'react';

export type OrientationType = 'portrait' | 'landscape';

interface OrientationState {
  isLandscape: boolean;
  orientation: OrientationType;
}

/**
 * Detects device orientation via matchMedia + Screen Orientation API.
 * Reusable across video capture, screen capture, etc.
 */
export const useOrientation = (): OrientationState => {
  const [orientation, setOrientation] = useState<OrientationType>(() => {
    if (typeof screen !== 'undefined' && screen.orientation) {
      return screen.orientation.type.startsWith('landscape') ? 'landscape' : 'portrait';
    }
    if (typeof window !== 'undefined') {
      return window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';
    }
    return 'portrait';
  });

  useEffect(() => {
    // Primary: Screen Orientation API
    const handleScreenOrientation = () => {
      const type = screen.orientation.type;
      setOrientation(type.startsWith('landscape') ? 'landscape' : 'portrait');
    };

    // Fallback: matchMedia
    const mql = window.matchMedia('(orientation: landscape)');
    const handleMediaChange = (e: MediaQueryListEvent) => {
      setOrientation(e.matches ? 'landscape' : 'portrait');
    };

    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleScreenOrientation);
    }
    mql.addEventListener('change', handleMediaChange);

    return () => {
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleScreenOrientation);
      }
      mql.removeEventListener('change', handleMediaChange);
    };
  }, []);

  return {
    isLandscape: orientation === 'landscape',
    orientation,
  };
};
